import { FC, memo, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useResponsive } from '../../../../hooks';
import { PCMenu, MobileMenu, PCItem } from './menu.styled';
import { Button, Text, SVG } from '../../components-v4';
import { ReactComponent as House } from '../../assets/house.svg';
import { ReactComponent as Accrual } from '../../assets/accrual.svg';
import { ReactComponent as Meter } from '../../assets/meter.svg';
import { ReactComponent as Star } from '../../assets/star.svg';
import {
  getPublicRequestData,
  getAccrualsData,
  getMetersData,
} from '../../selectors';

type MenuSection = 'request' | 'accruals' | 'meters' | 'rate';

const SECTIONS: MenuSection[] = ['rate', 'request', 'accruals', 'meters'];
const SCROLL_OFFSET = 150;

const findScrollContainer = (): HTMLElement | null => {
  const firstSection =
    document.getElementById('rate') ?? document.getElementById('request');
  if (!firstSection) return null;

  let parent = firstSection.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

export const Menu: FC = memo(() => {
  const { viewport } = useResponsive();

  const requestData = useSelector(getPublicRequestData);
  const accrualsData = useSelector(getAccrualsData);
  const metersData = useSelector(getMetersData);

  const [activeSection, setActiveSection] = useState<MenuSection>(
    requestData.status === 'performed' ? 'rate' : 'request'
  );
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const handleScroll = useCallback((id: MenuSection) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    let currentTarget: HTMLElement | Window | null = null;
    let currentHandler: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const createScrollHandler = () => {
      return () => {
        const container = scrollContainerRef.current;
        const scrollTop = container ? container.scrollTop : window.scrollY;

        for (let i = SECTIONS.length - 1; i >= 0; i--) {
          const element = document.getElementById(SECTIONS[i]);
          if (!element) continue;

          let offsetTop: number;

          if (container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            offsetTop =
              elementRect.top - containerRect.top + container.scrollTop;
          } else {
            offsetTop = element.offsetTop;
          }

          if (scrollTop + SCROLL_OFFSET >= offsetTop) {
            setActiveSection(SECTIONS[i]);
            break;
          }
        }
      };
    };

    const setup = () => {
      if (cancelled) return;

      // Убираем предыдущий слушатель, если был
      if (currentTarget && currentHandler) {
        currentTarget.removeEventListener('scroll', currentHandler);
      }

      const scrollContainer = findScrollContainer();
      scrollContainerRef.current = scrollContainer;
      currentTarget = scrollContainer ?? window;
      currentHandler = createScrollHandler();

      currentTarget.addEventListener('scroll', currentHandler);
      currentHandler();

      // Если скролл-контейнер не найден (секции ещё не в DOM), пробуем снова
      if (!scrollContainer && !cancelled) {
        retryTimer = setTimeout(setup, 200);
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (currentTarget && currentHandler) {
        currentTarget.removeEventListener('scroll', currentHandler);
      }
    };
  }, [requestData.status]);

  if (viewport.desktop) {
    return (
      <PCMenu>
        {requestData.status === 'performed' && (
          <PCItem
            isSelected={activeSection === 'rate'}
            onClick={() => handleScroll('rate')}
          >
            <SVG src={Star} />
            <Text variant="body-14" color="gray.800">
              Оценка качества
            </Text>
          </PCItem>
        )}
        <PCItem
          isSelected={activeSection === 'request'}
          onClick={() => handleScroll('request')}
        >
          <SVG src={House} />
          <Text variant="body-14" color="gray.800">
            Заявка
          </Text>
        </PCItem>
        {!!accrualsData.length && (
          <PCItem
            isSelected={activeSection === 'accruals'}
            onClick={() => handleScroll('accruals')}
          >
            <SVG src={Accrual} />
            <Text variant="body-14" color="gray.800">
              Начисления
            </Text>
          </PCItem>
        )}
        {!!metersData.length && (
          <PCItem
            isSelected={activeSection === 'meters'}
            onClick={() => handleScroll('meters')}
          >
            <SVG src={Meter} />
            <Text variant="body-14" color="gray.800">
              Счётчики
            </Text>
          </PCItem>
        )}
      </PCMenu>
    );
  }

  return (
    <MobileMenu isTablet={viewport.tablet}>
      {requestData.status === 'performed' && (
        <Button
          mode={activeSection === 'rate' ? 'additional' : 'neutral'}
          variant="main"
          size={viewport.tablet ? 'm' : 'xs'}
          onClick={() => handleScroll('rate')}
        >
          Оценка качества
        </Button>
      )}
      <Button
        mode="neutral"
        variant="main"
        selected={activeSection === 'request'}
        size={viewport.tablet ? 'm' : 'xs'}
        onClick={() => handleScroll('request')}
      >
        Заявка
      </Button>
      {!!accrualsData.length && (
        <Button
          mode="neutral"
          variant="main"
          selected={activeSection === 'accruals'}
          size={viewport.tablet ? 'm' : 'xs'}
          onClick={() => handleScroll('accruals')}
        >
          Начисления
        </Button>
      )}
      {!!metersData.length && (
        <Button
          mode="neutral"
          variant="main"
          selected={activeSection === 'meters'}
          size={viewport.tablet ? 'm' : 'xs'}
          onClick={() => handleScroll('meters')}
        >
          Счётчики
        </Button>
      )}
    </MobileMenu>
  );
});
